import React, { useState, useEffect, useMemo } from "react";
import DashboardLayout from "../../layoutcontainers/DashboardLayout";
import BASEURL from "../Utils/Baseurl";
import axios from "axios";
import {
  Box,
  Grid,
  Card,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  CircularProgress,
  Stack,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import DateRangeIcon from "@mui/icons-material/DateRange";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import RefreshIcon from "@mui/icons-material/Refresh";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import { useUserDetails } from "../../context/auth";

// Pastel colors generator for employee avatars
const AVATAR_PALETTES = [
  { bg: "#E0F2FE", text: "#0284C7" }, // Sky blue
  { bg: "#EDE9FE", text: "#7C3AED" }, // Purple
  { bg: "#ECFDF5", text: "#059669" }, // Emerald
  { bg: "#FEF3C7", text: "#D97706" }, // Amber
  { bg: "#FCE7F3", text: "#DB2777" }, // Pink
  { bg: "#CCFBF1", text: "#0D9488" }, // Teal
  { bg: "#DBEAFE", text: "#1D4ED8" }, // Royal blue
  { bg: "#FEE2E2", text: "#DC2626" }, // Rose
];

const getAvatarPalette = (str = "") => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % AVATAR_PALETTES.length;
  return AVATAR_PALETTES[index];
};

const getInitials = (firstName = "", lastName = "", empId = "") => {
  const f = (firstName || "").trim()[0] || "";
  const l = (lastName || "").trim()[0] || "";
  if (f || l) {
    return `${f}${l}`.toUpperCase();
  }
  return (empId || "EM").slice(0, 2).toUpperCase();
};

const EMPLOYEES_PER_PAGE = 6;

const LeaveCount = () => {
  const [allEmployees, setAllEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [canViewCount, setCanViewCount] = useState(false);
  const { AccessData } = useUserDetails();

  useEffect(() => {
    setCanViewCount(
      Array.isArray(AccessData) &&
        AccessData.some(
          (item) =>
            item.module === "LeaveCount" &&
            item.component === "ViewLeaveCount" &&
            item.can_access
        )
    );
  }, [AccessData]);

  const fetchAllEmployeeLeaveDetails = async () => {
    setLoading(true);
    const query = `
      query AllEmployeeLeaveDetails {
        allEmployeeLeaveDetails {
          empid
          firstname
          lastname
          leaveDetails {
            leaveType
            total
            taked
            balance
          }
        }
      }
    `;

    try {
      const response = await axios.post(
        BASEURL,
        { query },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const data = response.data?.data?.allEmployeeLeaveDetails || [];
      setAllEmployees(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Unable to fetch employee leave details", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllEmployeeLeaveDetails();
  }, []);

  // Filter employees by name or empid
  const filteredEmployees = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return allEmployees;

    return allEmployees.filter((emp) => {
      const firstName = (emp.firstname || "").toLowerCase();
      const lastName = (emp.lastname || "").toLowerCase();
      const fullName = `${firstName} ${lastName}`.trim();
      const empId = (emp.empid || "").toString().toLowerCase();

      return (
        fullName.includes(term) ||
        firstName.includes(term) ||
        lastName.includes(term) ||
        empId.includes(term)
      );
    });
  }, [allEmployees, searchTerm]);

  // Reset to page 1 whenever search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Aggregate summary metrics based on filtered employees
  const summaryMetrics = useMemo(() => {
    let totalEmployees = filteredEmployees.length;
    let totalTaken = 0;
    let totalAvailable = 0;

    filteredEmployees.forEach((emp) => {
      const leaves = emp.leaveDetails || [];
      leaves.forEach((l) => {
        totalTaken += Number(l.taked) || 0;
        totalAvailable += Number(l.balance) || 0;
      });
    });

    return {
      totalEmployees,
      totalTaken,
      totalAvailable,
    };
  }, [filteredEmployees]);

  // Pagination calculation
  const totalPages = Math.ceil(filteredEmployees.length / EMPLOYEES_PER_PAGE) || 1;
  const startIndex = (currentPage - 1) * EMPLOYEES_PER_PAGE;
  const endIndex = Math.min(startIndex + EMPLOYEES_PER_PAGE, filteredEmployees.length);
  const currentEmployees = filteredEmployees.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <DashboardLayout>
        
        <Card
          sx={{
            p: { xs: 2, sm: 3, md: 3.5 },
            backgroundColor: "#FFFFFF",
            borderRadius: "18px",
            border: "1px solid #E2E8F0",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
          }}
        >
          {/* 1. Dashboard Header */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "stretch", md: "center" },
              justifyContent: "space-between",
              gap: 2,
              mb: 3.5,
            }}
          >
            {/* Title & Subtitle with Icon */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                sx={{
                  width: { xs: 42, sm: 48 },
                  height: { xs: 42, sm: 48 },
                  borderRadius: "14px",
                  backgroundColor: "#EFF6FF",
                  border: "1px solid #BFDBFE",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#2563EB",
                  flexShrink: 0,
                }}
              >
                <DateRangeIcon/>
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontSize: { xs: "1rem", sm: "1.2rem" },
                    fontWeight: 700,
                    color: "#0F172A",
                    lineHeight: 1.2,
                  }}
                >
                  Leave Count
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: "0.7rem", sm: "0.875rem" },
                    color: "#64748B",
                    fontWeight: 400,
                    mt: 0.4,
                  }}
                >
                  Track leave usage and available days by employee
                </Typography>
              </Box>
            </Box>

            {/* Right: Search Input */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                width: { xs: "100%", md: "auto" },
              }}
            >
              <TextField
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search employee..."
                size="small"
                sx={{
                  width: { xs: "100%", sm: 280, md: 320 },
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#FFFFFF",
                    borderRadius: "10px",
                    fontSize: "0.875rem",
                    "& fieldset": {
                      borderColor: "#CBD5E1",
                    },
                    "&:hover fieldset": {
                      borderColor: "#94A3B8",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#2563EB",
                      borderWidth: "1.5px",
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "#94A3B8", fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearchTerm("")}
                        edge="end"
                      >
                        <ClearIcon sx={{ fontSize: 16, color: "#94A3B8" }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />

              <IconButton
                onClick={fetchAllEmployeeLeaveDetails}
                title="Refresh Data"
                sx={{
                  border: "1px solid #CBD5E1",
                  borderRadius: "10px",
                  p: 1.1,
                  color: "#475569",
                  "&:hover": {
                    backgroundColor: "#F1F5F9",
                    color: "#2563EB",
                  },
                }}
              >
                <RefreshIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>
          </Box>

          {/* 2. Summary Cards */}
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            {/* Total Employees */}
            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  backgroundColor: '#F0FDF4',
                  border: '1px solid #BBF7D0',
                  borderRadius: '10px',
                  px: 1.5,
                  py: 0.8,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.2,
                }}
              >
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: '50%',
                    backgroundColor: '#DCFCE7',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#16A34A',
                    flexShrink: 0,
                  }}
                >
                  <PeopleAltIcon sx={{ fontSize: 19 }} />
                </Box>

                <Box>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#475569', lineHeight: 1.2 }}>
                    Total Employees
                  </Typography>
                  <Typography sx={{ fontSize: '1.25rem', fontWeight: 800, color: '#15803D', lineHeight: 1.3 }}>
                    {summaryMetrics.totalEmployees}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Total Days Taken */}
            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  backgroundColor: '#FFFBEB',
                  border: '1px solid #FDE68A',
                  borderRadius: '10px',
                  px: 1.5,
                  py: 0.8,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.2,
                }}
              >
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: '50%',
                    backgroundColor: '#FEF3C7',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#D97706',
                    flexShrink: 0,
                  }}
                >
                  <EventAvailableIcon sx={{ fontSize: 19 }} />
                </Box>

                <Box>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#475569', lineHeight: 1.2 }}>
                    Total Days Taken
                  </Typography>
                  <Typography sx={{ fontSize: '1.25rem', fontWeight: 800, color: '#B45309', lineHeight: 1.3 }}>
                    {summaryMetrics.totalTaken}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Total Days Available */}
            {/* <Grid item xs={12} sm={4}>
              <Box
                sx={{
                  backgroundColor: '#EFF6FF',
                  border: '1px solid #BFDBFE',
                  borderRadius: '10px',
                  px: 1.5,
                  py: 0.8,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.2,
                }}
              >
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: '50%',
                    backgroundColor: '#DBEAFE',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#2563EB',
                    flexShrink: 0,
                  }}
                >
                  <AccessTimeIcon sx={{ fontSize: 19 }} />
                </Box>

                <Box>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#475569', lineHeight: 1.2 }}>
                    Total Days Available
                  </Typography>
                  <Typography sx={{ fontSize: '1.25rem', fontWeight: 800, color: '#1D4ED8', lineHeight: 1.3 }}>
                    {summaryMetrics.totalAvailable}
                  </Typography>
                </Box>
              </Box>
            </Grid> */}
          </Grid>

          {/* 3. Employee Leave Cards / Loading / Empty States */}
          
          {loading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 12,
                gap: 2,
              }}
            >
              <CircularProgress size={44} sx={{ color: "#2563EB" }} />
              <Typography sx={{ color: "#64748B", fontSize: "0.95rem" }}>
                Loading leave details...
              </Typography>
            </Box>
          ) : filteredEmployees.length === 0 ? (
            /* Empty State */
            <Box
              sx={{
                textAlign: "center",
                py: 8,
                px: 2,
                borderRadius: "16px",
                backgroundColor: "#F8FAFC",
                border: "1px dashed #CBD5E1",
              }}
            >
              <SearchIcon sx={{ fontSize: 48, color: "#94A3B8", mb: 1.5 }} />
              <Typography
                sx={{
                  fontSize: "1.1rem",
                  fontWeight: 700,
                  color: "#1E293B",
                  mb: 0.5,
                }}
              >
                {searchTerm
                  ? `No employees found matching "${searchTerm}"`
                  : "No employee leave records found"}
              </Typography>
              <Typography sx={{ fontSize: "0.875rem", color: "#64748B", mb: 2 }}>
                {searchTerm
                  ? "Try searching with a different name or employee ID."
                  : "Leave details will appear here once configured."}
              </Typography>
              {searchTerm && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setSearchTerm("")}
                  sx={{
                    borderColor: "#2563EB",
                    color: "#2563EB",
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Clear Search
                </Button>
              )}
            </Box>
          ) : (
            /* Responsive Grid of Employee Cards */
            <Grid container spacing={2.5}>
              {currentEmployees.map((employee, empIndex) => {
                const fullName =
                  `${employee.firstname || ""} ${employee.lastname || ""}`.trim() ||
                  "Unnamed Employee";
                const empId = employee.empid || `EMP${empIndex + 1}`;
                const leaveDetails = employee.leaveDetails || [];
                const initials = getInitials(
                  employee.firstname,
                  employee.lastname,
                  employee.empid
                );
                const palette = getAvatarPalette(fullName + empId);

                // Total remaining leave days
                const totalBalance = leaveDetails.reduce(
                  (sum, l) => sum + (Number(l.balance) || 0),
                  0
                );

                // Days left badge styling
                let badgeBg = "#ECFDF5";
                let badgeBorder = "#A7F3D0";
                let badgeText = "#059669";
                if (totalBalance <= 0) {
                  badgeBg = "#FEF2F2";
                  badgeBorder = "#FECACA";
                  badgeText = "#DC2626";
                } else if (totalBalance <= 10) {
                  badgeBg = "#FFFBEB";
                  badgeBorder = "#FDE68A";
                  badgeText = "#D97706";
                }

                return (
                  <Grid item xs={12} sm={6} lg={4} key={employee.empid || empIndex}>
                    <Box
                      sx={{
                        backgroundColor: "#FFFFFF",
                        border: "1px solid #E2E8F0",
                        borderRadius: "16px",
                        p: 2.5,
                        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 8px 20px rgba(15, 23, 42, 0.08)",
                          borderColor: "#CBD5E1",
                        },
                      }}
                    >
                      {/* Card Header: Avatar, Name, EMPID, and Days Left Badge */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          mb: 2.5,
                        }}
                      >
                        {/* Avatar & Employee Info */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            minWidth: 0,
                          }}
                        >
                          <Box
                            sx={{
                              width: 38,
                              height: 38,
                              borderRadius: "50%",
                              backgroundColor: palette.bg,
                              color: palette.text,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "0.95rem",
                              fontWeight: 700,
                              flexShrink: 0,
                            }}
                          >
                            {initials}
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              noWrap
                              sx={{
                                fontSize: "0.8rem",
                                fontWeight: 700,
                                color: "#0F172A",
                                lineHeight: 1.25,
                              }}
                            >
                              {fullName}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.7rem",
                                color: "#94A3B8",
                                fontWeight: 500,
                                mt: 0.2,
                              }}
                            >
                              {empId}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Remaining Leave Badge */}
                        {/* <Box
                          sx={{
                            backgroundColor: badgeBg,
                            border: `1px solid ${badgeBorder}`,
                            borderRadius: "12px",
                            px: 1.5,
                            py: 0.6,
                            textAlign: "center",
                            minWidth: 64,
                            flexShrink: 0,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "0.8rem",
                              fontWeight: 800,
                              color: badgeText,
                              lineHeight: 1.1,
                            }}
                          >
                            {totalBalance}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.6rem",
                              fontWeight: 600,
                              color: badgeText,
                              lineHeight: 1,
                              mt: 0.2,
                              textTransform: "lowercase",
                            }}
                          >
                            days left
                          </Typography>
                        </Box> */}
                      </Box>

                      {/* Leave Types Breakdown */}
                      <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", gap: 1.8 }}>
                        {leaveDetails.length === 0 ? (
                          <Typography
                            sx={{
                              fontSize: "0.8rem",
                              color: "#94A3B8",
                              fontStyle: "italic",
                              py: 2,
                              textAlign: "center",
                            }}
                          >
                            No leave details allocated
                          </Typography>
                        ) : (
                          leaveDetails.map((leave, lIdx) => {
                            const total = Number(leave.total) || 0;
                            const taken = Number(leave.taked) || 0;
                            const percentage =
                              total > 0 ? Math.round((taken / total) * 100) : 0;
                            const barWidth = Math.min(100, Math.max(0, percentage));

                            // Color & status determination
                            let statusLabel = "Available";
                            let barColor = "#10B981"; // Fresh green
                            let statusBg = "#ECFDF5";
                            let statusBorder = "#A7F3D0";
                            let statusColor = "#059669";

                            if (total === 0) {
                              statusLabel = "Not Allocated";
                              barColor = "#CBD5E1";
                              statusBg = "#F1F5F9";
                              statusBorder = "#E2E8F0";
                              statusColor = "#64748B";
                            } else if (percentage >= 100 || taken >= total) {
                              statusLabel = "Fully used";
                              barColor = "#EF4444"; // Red
                              statusBg = "#FEF2F2";
                              statusBorder = "#FECACA";
                              statusColor = "#DC2626";
                            } else if (percentage >= 80) {
                              statusLabel = "Nearly used";
                              barColor = "#F59E0B"; // Amber
                              statusBg = "#FFFBEB";
                              statusBorder = "#FDE68A";
                              statusColor = "#D97706";
                            }

                            return (
                              <Box key={lIdx}>
                                {/* Row 1: Leave Type Name & Taken / Total */}
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    mb: 0.6,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: "0.7rem",
                                      fontWeight: 600,
                                      color: "#1E293B",
                                    }}
                                  >
                                    {leave.leaveType || "Leave"}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: "0.7rem",
                                      fontWeight: 600,
                                      color: "#334155",
                                    }}
                                  >
                                    {taken} / {total} days
                                  </Typography>
                                </Box>

                                {/* Row 2: Progress Bar, Percentage, and Status Label */}
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.2,
                                  }}
                                >
                                  {/* Progress bar track */}
                                  <Box
                                    sx={{
                                      flex: 1,
                                      height: 7,
                                      borderRadius: 4,
                                      backgroundColor: "#F1F5F9",
                                      overflow: "hidden",
                                      position: "relative",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        width: `${barWidth}%`,
                                        height: "100%",
                                        borderRadius: 4,
                                        backgroundColor: barColor,
                                        transition: "width 0.4s ease-in-out",
                                      }}
                                    />
                                  </Box>

                                  {/* Percentage text */}
                                  {/* <Typography
                                    sx={{
                                      fontSize: "0.75rem",
                                      fontWeight: 600,
                                      color: "#64748B",
                                      minWidth: 32,
                                      textAlign: "right",
                                    }}
                                  >
                                    {percentage}%
                                  </Typography> */}

                                  {/* Status label pill */}
                                  <Box
                                    sx={{
                                      backgroundColor: statusBg,
                                      border: `1px solid ${statusBorder}`,
                                      borderRadius: "6px",
                                      px: 1,
                                      py: 0.25,
                                      minWidth: 78,
                                      textAlign: "center",
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: "0.6875rem",
                                        fontWeight: 600,
                                        color: statusColor,
                                        lineHeight: 1.2,
                                      }}
                                    >
                                      {statusLabel}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            );
                          })
                        )}
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          )}

          {/* 4. Footer & Pagination Controls */}
          {!loading && filteredEmployees.length > 0 && (
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                mt: 4,
                pt: 2.5,
                borderTop: "1px solid #F1F5F9",
              }}
            >
              {/* Left: Showing entries count */}
              <Typography
                sx={{
                  fontSize: "0.8125rem",
                  color: "#64748B",
                  fontWeight: 500,
                }}
              >
                Showing {startIndex + 1} – {endIndex} of{" "}
                {filteredEmployees.length} employees
              </Typography>

              {/* Right: Custom styled Pagination buttons matching design */}
              <Stack direction="row" spacing={1} alignItems="center">
                {/* Prev button */}
                <IconButton
                  size="small"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  sx={{
                    border: "1px solid #E2E8F0",
                    borderRadius: "8px",
                    p: 0.75,
                    color: currentPage === 1 ? "#CBD5E1" : "#475569",
                    backgroundColor: "#FFFFFF",
                    "&:hover": {
                      backgroundColor: "#F8FAFC",
                    },
                    "&.Mui-disabled": {
                      borderColor: "#F1F5F9",
                      color: "#CBD5E1",
                    },
                  }}
                >
                  <ChevronLeftIcon sx={{ fontSize: 18 }} />
                </IconButton>

                {/* Page number buttons */}
                {(() => {
                  const pages = [];
                  const siblingCount = 1;

                  if (totalPages <= 1) {
                    return null;
                  }

                  pages.push(1);

                  const startPage = Math.max(2, currentPage - siblingCount);
                  const endPage = Math.min(totalPages - 1, currentPage + siblingCount);

                  if (startPage > 2) {
                    pages.push("start-ellipsis");
                  }

                  for (let page = startPage; page <= endPage; page++) {
                    pages.push(page);
                  }

                  if (endPage < totalPages - 1) {
                    pages.push("end-ellipsis");
                  }

                  pages.push(totalPages);

                  return pages.map((pageNum) => {
                    if (typeof pageNum === "string") {
                      return (
                        <Box
                          key={pageNum}
                          sx={{
                            minWidth: 20,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#94A3B8",
                          }}
                        >
                          ...
                        </Box>
                      );
                    }

                    const isActive = pageNum === currentPage;

                    return (
                      <Button
                        key={pageNum}
                        size="small"
                        onClick={() => handlePageChange(pageNum)}
                        sx={{
                          minWidth: 34,
                          height: 34,
                          p: 0,
                          borderRadius: "8px",
                          fontSize: "0.8125rem",
                          fontWeight: isActive ? 700 : 500,
                          backgroundColor: isActive ? "#2563EB" : "#FFFFFF",
                          color: isActive ? "#FFFFFF" : "#475569",
                          border: isActive ? "none" : "1px solid #E2E8F0",
                          boxShadow: isActive
                            ? "0 2px 6px rgba(37, 99, 235, 0.3)"
                            : "none",
                          "&:hover": {
                            backgroundColor: isActive ? "#1D4ED8" : "#F8FAFC",
                          },
                        }}
                      >
                        {pageNum}
                      </Button>
                    );
                  });
                })()}

                {/* Next button */}
                <IconButton
                  size="small"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  sx={{
                    border: "1px solid #E2E8F0",
                    borderRadius: "8px",
                    p: 0.75,
                    color: currentPage === totalPages ? "#CBD5E1" : "#475569",
                    backgroundColor: "#FFFFFF",
                    "&:hover": {
                      backgroundColor: "#F8FAFC",
                    },
                    "&.Mui-disabled": {
                      borderColor: "#F1F5F9",
                      color: "#CBD5E1",
                    },
                  }}
                >
                  <ChevronRightIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Stack>
            </Box>
          )}
        </Card>
   
    </DashboardLayout>
  );
};

export default LeaveCount;
